import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiError,
  type Company,
  type DocumentType,
  type NewProduct,
  type NumberingReset,
  type NumberingSeries,
  type Product,
  type VatRateCode,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, currencyLabel } from '../format';
import * as Icon from '../components/Icons';

const VAT_RATES: VatRateCode[] = ['23', '8', '5', '0', 'zw', 'np', 'oo'];

const emptyProduct: NewProduct = {
  code: '',
  names: { pl: '' },
  unit: 'szt.',
  vatRate: '23',
  gtuCode: '',
  pkwiu: '',
  cn: '',
  defaultPrice: '',
  currency: 'PLN',
  active: true,
};

/**
 * Cena brutto pozycji.
 *
 * Przy wystawianiu faktury osobie fizycznej to ona jest kwotą, o którą pyta klient — dlatego
 * liczymy ją tu, a nie każemy jej sobie wyobrażać. Stawki nieliczbowe (zw., np., oo) nie
 * dokładają podatku, więc brutto równa się netto.
 */
function grossOf(net: string | null, rate: VatRateCode): number | null {
  if (!net) return null;
  const value = Number(net.replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(value)) return null;
  const percent = Number(rate);
  return Number.isFinite(percent) ? value * (1 + percent / 100) : value;
}

export function ProductsPanel({ company, t, locale }: { company: Company; t: Dictionary; locale: Locale }) {
  const [items, setItems] = useState<Product[] | null>(null);
  const [query, setQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [form, setForm] = useState<NewProduct | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(await api.products(company.id, query, includeInactive));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id, query, includeInactive]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), 250);
    return () => clearTimeout(timer);
  }, [load]);

  const editable = !company.roles.every((role) => role === 'VIEWER');

  function closeForm() {
    setForm(null);
    setEditingId(null);
  }

  function edit(item: Product) {
    const { id, ...fields } = item;
    setForm({ ...fields, names: { ...fields.names } });
    setEditingId(id);
    setError(null);
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!form) return;
    setBusy(true);
    setError(null);
    try {
      // Puste pola wysyłamy jako brak wartości; nazwy bez treści nie mają trafić do kartoteki
      const product: NewProduct = {
        ...form,
        code: form.code?.trim() || null,
        gtuCode: form.gtuCode?.trim() || null,
        defaultPrice: form.defaultPrice?.trim().replace(/\s/g, '').replace(',', '.') || null,
        names: Object.fromEntries(Object.entries(form.names)
          .filter(([, value]) => value.trim())
          .map(([language, value]) => [language, value.trim()])),
      };
      if (editingId) await api.updateProduct(company.id, editingId, product);
      else await api.addProduct(company.id, product);
      closeForm();
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  if (form) {
    return (
      <>
        <div className="hd">
          <div>
            <h3>{editingId ? t.products.edit : t.products.add}</h3>
            <p>{t.products.lead}</p>
          </div>
        </div>
        {error && (
          <div className="alert a-bad">
            <Icon.Error />
            <div>
              <b>{error}</b>
            </div>
          </div>
        )}
        <form className="panel" onSubmit={save}>
          <div className="fields">
            <div className="field">
              <label htmlFor="pName">{t.products.namePl}</label>
              <input
                id="pName"
                className="inp"
                value={form.names.pl ?? ''}
                onChange={(event) => setForm({ ...form, names: { ...form.names, pl: event.target.value } })}
                required={!Object.entries(form.names).some(([language, value]) =>
                  language !== 'pl' && value.trim())}
              />
            </div>
            <div className="field">
              <label htmlFor="pNameEn">{t.products.nameEn}</label>
              <input
                id="pNameEn"
                className="inp"
                value={form.names.en ?? ''}
                onChange={(event) => setForm({ ...form, names: { ...form.names, en: event.target.value } })}
              />
            </div>
            <div className="field">
              <label htmlFor="pCode">{t.products.code}</label>
              <input
                id="pCode"
                className="inp mono"
                value={form.code ?? ''}
                onChange={(event) => setForm({ ...form, code: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="pUnit">{t.products.unit}</label>
              <input
                id="pUnit"
                className="inp"
                value={form.unit}
                onChange={(event) => setForm({ ...form, unit: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="pRate">{t.products.vatRate}</label>
              <select
                id="pRate"
                className="inp"
                value={form.vatRate}
                onChange={(event) => setForm({ ...form, vatRate: event.target.value as VatRateCode })}
              >
                {VAT_RATES.map((rate) => (
                  <option key={rate} value={rate}>
                    {t.products.rates[rate]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="pPrice">{t.products.price}</label>
              <input
                id="pPrice"
                className="inp num"
                value={form.defaultPrice ?? ''}
                onChange={(event) => setForm({ ...form, defaultPrice: event.target.value })}
                inputMode="decimal"
                placeholder="0.00"
              />
              <span className="hint">
                {t.products.grossPreview}:{' '}
                {amount(grossOf(form.defaultPrice ?? null, form.vatRate), locale)}
              </span>
            </div>
            <div className="field">
              <label htmlFor="pGtu">{t.products.gtu}</label>
              <input
                id="pGtu"
                className="inp mono"
                value={form.gtuCode ?? ''}
                onChange={(event) => setForm({ ...form, gtuCode: event.target.value })}
                placeholder="GTU_01"
              />
            </div>
            {editingId && (
              <label className="check">
                <input type="checkbox" checked={form.active}
                  onChange={(event) => setForm({ ...form, active: event.target.checked })} />
                {t.counterparties.active}
              </label>
            )}
          </div>
          <div className="bar2">
            <button type="submit" className="btn" disabled={busy}>
              {busy ? t.companies.saving : t.products.save}
            </button>
            <div className="sp">
              <button type="button" className="btn ghost" onClick={closeForm} disabled={busy}>
                {t.companies.cancel}
              </button>
            </div>
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.products.title}</h3>
          <p>{t.products.lead}</p>
        </div>
        {editable && (
          <div className="sp">
            <button type="button" className="btn" onClick={() => {
              setEditingId(null);
              setForm({ ...emptyProduct, names: { ...emptyProduct.names } });
              setError(null);
            }}>
              <Icon.Plus />
              {t.products.add}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="row">
          <input
            className="inp"
            style={{ flex: '1 1 260px' }}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t.products.search}
            aria-label={t.products.search}
          />
          <label className="check">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            />
            {t.products.showInactive}
          </label>
          <span className="sm muted" style={{ marginLeft: 'auto' }}>
            {items?.length ?? 0}
          </span>
        </div>

        {items?.length === 0 ? (
          <div className="empty">
            <Icon.Goods />
            <b>{t.products.none}</b>
          </div>
        ) : (
          items && (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.products.namePl}</th>
                    <th>{t.products.code}</th>
                    <th>{t.products.unit}</th>
                    <th className="n">{t.products.vatRate}</th>
                    <th className="n">
                      {t.products.price} ({currencyLabel(items[0]?.currency ?? 'PLN', locale)})
                    </th>
                    <th className="n">
                      {t.products.grossPrice} ({currencyLabel(items[0]?.currency ?? 'PLN', locale)})
                    </th>
                    <th>{t.costs.status}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.names.pl ?? Object.values(item.names)[0]}</td>
                      {/* Pusty indeks pokazujemy myślnikiem — pusta komórka wygląda jak uszkodzony wiersz */}
                      <td className="mono">{item.code ?? '—'}</td>
                      <td>{item.unit}</td>
                      <td className="n">{t.products.rates[item.vatRate]}</td>
                      <td className="n">{amount(item.defaultPrice, locale)}</td>
                      <td className="n">{amount(grossOf(item.defaultPrice, item.vatRate), locale)}</td>
                      <td>
                        <span className={item.active ? 'chip st-ok' : 'chip st-draft'}>
                          <i />
                          {item.active ? t.counterparties.active : t.counterparties.inactive}
                        </span>
                      </td>
                      <td className="act">
                        <div className="row">
                          {editable && (
                            <button type="button" className="btn ghost sm" disabled={busy}
                              onClick={() => edit(item)}>{t.products.edit}</button>
                          )}
                          {editable && item.active && (
                            <button
                              type="button"
                              className="btn ghost sm"
                              disabled={busy}
                              onClick={() =>
                                void api
                                  .deactivateProduct(company.id, item.id)
                                  .then(load)
                                  .catch((failure: unknown) =>
                                    setError(failure instanceof ApiError ? failure.message : String(failure)),
                                  )
                              }
                            >
                              {t.products.retire}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </>
  );
}

/**
 * Wzorzec z miejscem na miesiąc.
 *
 * Przy przejściu na numerację miesięczną sam wzorzec zwykle miesiąca nie ma, a bez niego
 * styczniowa FV/1/2026 wróciłaby w lutym jako drugi dokument o tym samym numerze — serwer
 * taki wzorzec odrzuca. Zamiast pokazywać błąd, dokładamy {mm} przed rokiem: tak numer wygląda
 * na fakturach, które ludzie znają.
 */
export function withMonthMarker(pattern: string): string {
  if (/\{(mm|mr|msc|m)\}/.test(pattern)) return pattern;
  if (pattern.includes('{rok}')) return pattern.replace('{rok}', '{mm}/{rok}');
  if (pattern.includes('{rr}')) return pattern.replace('{rr}', '{mm}/{rr}');
  return `${pattern}/{mm}`;
}

type NumberingDraft = { pattern: string; reset: NumberingReset };

export function NumberingPanel({ company, t }: { company: Company; t: Dictionary }) {
  const year = new Date().getFullYear();
  const [series, setSeries] = useState<NumberingSeries[] | null>(null);
  const [draft, setDraft] = useState<Record<string, NumberingDraft>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const loaded = await api.numbering(company.id, year);
      setSeries(loaded);
      setDraft(
        Object.fromEntries(loaded.map((item) => [item.documentType, { pattern: item.pattern, reset: item.reset }])),
      );
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id, year]);

  useEffect(() => {
    void load();
  }, [load]);

  const editable = company.roles.some((role) => role === 'OWNER' || role === 'ADMIN' || role === 'ACCOUNTANT');

  function change(documentType: DocumentType, next: Partial<NumberingDraft>) {
    setDraft((current) => {
      const merged = { ...current[documentType], ...next };
      // Przejście na numerację miesięczną dokłada miesiąc do wzorca, jeśli go w nim nie było
      return {
        ...current,
        [documentType]:
          next.reset === 'MONTH' ? { ...merged, pattern: withMonthMarker(merged.pattern) } : merged,
      };
    });
  }

  async function save(documentType: DocumentType) {
    const edited = draft[documentType];
    if (!edited) return;
    setBusy(true);
    setError(null);
    try {
      await api.setNumbering(company.id, documentType, year, edited.pattern, edited.reset);
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="hd">
        <div>
          <h3>
            {t.numbering.title} — {year}
          </h3>
          <p>{t.numbering.lead}</p>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="alert a-info">
          <Icon.Info />
          <div>
            <b>{t.numbering.markers}</b>
            <p>{t.numbering.hint}</p>
          </div>
        </div>

        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>{t.numbering.document}</th>
                <th>{t.numbering.pattern}</th>
                <th>{t.numbering.reset}</th>
                <th>{t.numbering.next}</th>
                <th className="n">{t.numbering.issued}</th>
                <th>{t.costs.status}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {series?.map((item) => {
                // Numerację da się zmieniać, dopóki w danym roku nie wyszedł żaden dokument
                // tego typu — dotąd ta reguła była tylko opisana w zdaniu nad tabelą.
                // Liczbę zużytych numerów bierzemy z serwera: przy numeracji miesięcznej
                // liczników jest dwanaście i „następny numer" nie mówi nic o całym roku.
                const locked = item.issued > 0;
                const edited = draft[item.documentType];
                return (
                  <tr key={item.documentType}>
                    <td>{t.numbering.types[item.documentType]}</td>
                    <td>
                      <input
                        className="inp mono sm"
                        style={{ maxWidth: 200 }}
                        value={edited?.pattern ?? ''}
                        onChange={(event) => change(item.documentType, { pattern: event.target.value })}
                        disabled={!editable || locked}
                        aria-label={`${t.numbering.pattern} — ${t.numbering.types[item.documentType]}`}
                      />
                    </td>
                    <td>
                      <select
                        className="inp sm"
                        style={{ maxWidth: 160 }}
                        value={edited?.reset ?? 'YEAR'}
                        onChange={(event) =>
                          change(item.documentType, { reset: event.target.value as NumberingReset })
                        }
                        disabled={!editable || locked}
                        aria-label={`${t.numbering.reset} — ${t.numbering.types[item.documentType]}`}
                      >
                        <option value="YEAR">{t.numbering.resetYear}</option>
                        <option value="MONTH">{t.numbering.resetMonth}</option>
                      </select>
                    </td>
                    <td className="mono">{item.preview}</td>
                    <td className="n">{item.issued}</td>
                    <td>
                      <span className={locked ? 'chip st-draft' : 'chip st-ok'}>
                        <i />
                        {locked ? t.numbering.locked : t.numbering.changeable}
                      </span>
                    </td>
                    <td className="act">
                      <div className="row">
                        {editable &&
                          !locked &&
                          (edited?.pattern !== item.pattern || edited?.reset !== item.reset) && (
                          <button
                            type="button"
                            className="btn ghost sm"
                            disabled={busy}
                            onClick={() => void save(item.documentType)}
                          >
                            {t.numbering.save}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
